import {
  AssessmentSelectDto,
  AssessmentSubtype,
  AssessmentType,
  SurveyStatus,
  UserFeatureFlag,
  UserProfileRole,
} from '@@types/api.types';
import { SurveyCTA } from '@components/shared/survey-cta.component';
import { Tile } from '@components/shared/tile.component';
import { Skeleton } from '@components/ui/skeleton';
import { useSurveyNavigation } from '@hooks/surveys.hook';
import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import { useGetSurveys, useInnovatorCreateSurvey } from '@queries/survey.query';
import {
  useGetTechnology,
  useGetTechnologyMrlAssessment,
  useGetTechnologyTrlAssessment,
} from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { useQuery } from '@tanstack/react-query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { AnimatePresence, motion } from 'motion/react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IndividualMrlContent } from '../mrl/individual/individual-mrl-content.component';
import { MrlOverviewContent } from '../mrl/overview/mrl-overview-content.component';
import { MrlAssessmentHistoryList } from '../mrl/sidebar/mrl-assessment-history-list.component';
import { StartTRLModal } from '../technology-start-rapid-trl-modal.component';
import { IndividualTrlContent } from '../trl/individual/individual-trl-content.component';
import { Trl1Content } from '../trl/individual/trl1-content.component';
import { TrlOverviewContent } from '../trl/overview/trl-overview-content.component';
import { TrlProjectionContent } from '../trl/projection/trl-projection-content.component';
import { TRL1_ASSESSMENT_ID, TrlAssessmentHistoryList } from '../trl/sidebar/trl-assessment-history-list.component';
import { AccordionTile } from './accordion-tile.component';
import { scrollToNavOrTop } from './scroll.utils';
import {
  TECHNOLOGY_TAB_SIDEBAR_WIDTH_CLASS,
  TECHNOLOGY_TAB_STACK_LAYOUT_CLASS,
} from './technology-container-query.constants';
import { TechnologyTabLayout } from './technology-tab-layout.component';
import { useReadinessTab } from './use-readiness-tab';

type ReadinessTabProps = {
  type: 'mrl' | 'trl';
};

const ReadinessTabSkeleton: FC = () => {
  return (
    <TechnologyTabLayout
      className={TECHNOLOGY_TAB_STACK_LAYOUT_CLASS}
      mainContent={
        <div className="flex flex-col gap-4">
          <Tile>
            <Skeleton className="h-64 w-full" />
          </Tile>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Tile>
              <Skeleton className="h-48 w-full" />
            </Tile>
            <Tile>
              <Skeleton className="h-48 w-full" />
            </Tile>
          </div>
        </div>
      }
      sidebar={
        <>
          <Tile className="p-2">
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </Tile>
          <Tile className="p-3">
            <Skeleton className="h-10 w-full" />
          </Tile>
        </>
      }
      sidebarClassName={TECHNOLOGY_TAB_SIDEBAR_WIDTH_CLASS}
      variant="sidebar-content"
    />
  );
};

const READINESS_CONFIG = {
  mrl: {
    assessmentIdParam: 'mrlAssessmentId',
    assessmentType: AssessmentType.MRL,
    description:
      'Manufacture Readiness Level (MRL) is a measurement system used to assess the maturity of manufacturing technology, manufacturing processes, and the industrial base. The scale ranges from 1 to 10.',
    tabParam: 'mrlTab',
    title: 'Manufacture Readiness Level',
  },
  trl: {
    assessmentIdParam: 'assessmentId',
    assessmentType: AssessmentType.TRL,
    description:
      'Technology Readiness Level (TRL) is a measurement system used to assess the maturity of a particular technology. The scale ranges from 1 to 9, where TRL 1 represents basic principles observed and TRL 9 represents actual system proven through successful mission operations.',
    tabParam: 'trlTab',
    title: 'Technology Readiness Level',
  },
} as const;

const getLatestAssessment = (assessments: AssessmentSelectDto[]): AssessmentSelectDto | null => {
  if (assessments.length === 0) return null;

  return assessments.reduce<AssessmentSelectDto>((latest, assessment) => {
    const latestDate = new Date(latest.assessmentDate ?? latest.createdAt).getTime();
    const assessmentDate = new Date(assessment.assessmentDate ?? assessment.createdAt).getTime();
    return assessmentDate > latestDate ? assessment : latest;
  }, assessments[0]);
};

export const ReadinessTab: FC<ReadinessTabProps> = (props) => {
  const { type } = props;
  const config = READINESS_CONFIG[type];

  const { technologyId } = useParams<{ technologyId: string }>();
  const { activeTab, assessmentId, selectAssessment, setActiveTab } = useReadinessTab({
    assessmentIdParam: config.assessmentIdParam,
    tabParam: config.tabParam,
  });

  const previousActiveTabRef = useRef<string | null>(null);
  const previousAssessmentIdRef = useRef<string | null>(null);
  const sidebarNavAnchorRef = useRef<HTMLDivElement>(null);

  // TRL-specific state
  const [isTrlModalOpen, setIsTrlModalOpen] = useState(false);
  // MRL-specific state
  const [isCreatingMrl, setIsCreatingMrl] = useState(false);
  const { navigateToSurvey } = useSurveyNavigation();

  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const canSeeTrlProjection = Boolean(user?.featureFlags?.includes(UserFeatureFlag.TRL_PREDICTION));
  const { mutateAsync: createSurvey } = useInnovatorCreateSurvey();

  const isTrl1Selected = type === 'trl' && assessmentId === TRL1_ASSESSMENT_ID;

  const {
    data: technology,
    isError: isTechnologyError,
    isPending: isTechnologyPending,
  } = useGetTechnology(technologyId);
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = user?.profile?.role === UserProfileRole.INNOVATOR && !isChildOrganizationView;

  // Fetch draft surveys
  const { data: draftSurveys = [], isPending: isDraftSurveysPending } = useGetSurveys({
    status: SurveyStatus.DRAFT,
    technologyId: technology ? technologyId : undefined,
  });

  const draftSurvey = draftSurveys.find((survey) => {
    if (config.assessmentType === AssessmentType.TRL) {
      return survey.assessmentType === AssessmentType.TRL && survey.assessmentSubtype !== AssessmentSubtype.MILITARY;
    }
    return survey.assessmentType === config.assessmentType;
  });
  const hasDraftSurvey = Boolean(draftSurvey);
  const showStartButton = !hasDraftSurvey && !isDraftSurveysPending && canEdit;

  // Fetch assessments for history list
  const assessmentQueryOptions = useAssessmentsQueryOptions({
    technologyId: technology ? technologyId : undefined,
    type: [config.assessmentType],
  });
  const { data: assessments = [], isPending: isAssessmentsLoading } = useQuery(assessmentQueryOptions);
  const latestTrlAssessment = useMemo(() => getLatestAssessment(assessments), [assessments]);

  // Fetch featured assessment - type-specific hooks
  const { data: trlFeaturedAssessment, isPending: isTrlFeaturedAssessmentPending } = useGetTechnologyTrlAssessment(
    type === 'trl' && technology ? technologyId : undefined,
  );
  const { data: mrlFeaturedAssessment } = useGetTechnologyMrlAssessment(
    type === 'mrl' && technology ? technologyId : undefined,
  );
  const canRenderProjection = useMemo(() => {
    if (type !== 'trl') return false;
    if (!canSeeTrlProjection) return false;
    if (typeof trlFeaturedAssessment?.score !== 'number') return false;

    return trlFeaturedAssessment.score < 9;
  }, [canSeeTrlProjection, trlFeaturedAssessment?.score, type]);
  const featuredAssessmentId =
    type === 'trl' ? (trlFeaturedAssessment?.id ?? null) : (mrlFeaturedAssessment?.id ?? null);
  const effectiveTrlAssessmentId = assessmentId ?? latestTrlAssessment?.id ?? null;
  const hasExistingScore = assessments.length > 0;

  const hasNoAssessments = !isAssessmentsLoading && assessments.length === 0;

  // Scroll to top on tab/assessment change
  useEffect(() => {
    const previousActiveTab = previousActiveTabRef.current;
    if (previousActiveTab !== null && previousActiveTab !== activeTab) {
      scrollToNavOrTop({ anchorElement: sidebarNavAnchorRef.current });
    }
    previousActiveTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const previousAssessmentId = previousAssessmentIdRef.current;
    if (previousAssessmentId !== null && previousAssessmentId !== assessmentId) {
      scrollToNavOrTop({ anchorElement: sidebarNavAnchorRef.current });
    }
    previousAssessmentIdRef.current = assessmentId ?? null;
  }, [assessmentId]);

  useEffect(() => {
    if (type !== 'trl') return;
    if (activeTab !== 'projection') return;
    if (isTrlFeaturedAssessmentPending) return;
    if (canRenderProjection) return;
    setActiveTab('overview');
  }, [activeTab, canRenderProjection, isTrlFeaturedAssessmentPending, setActiveTab, type]);

  // MRL-specific start handler
  const handleStartMrl = async (): Promise<void> => {
    if (!technology?.id || isCreatingMrl) return;

    try {
      setIsCreatingMrl(true);
      const survey = await createSurvey({
        assessmentType: AssessmentType.MRL,
        technologyId: technology.id,
      });
      navigateToSurvey(`/technology/${technology.id}/survey/${survey.id}`);
    } finally {
      setIsCreatingMrl(false);
    }
  };

  // Early returns
  if (!technologyId || isTechnologyError) return null;

  if (isTechnologyPending || isAssessmentsLoading) {
    return <ReadinessTabSkeleton />;
  }

  // Render sidebar content
  const renderSidebar = () => {
    return (
      <>
        {canEdit && (
          <SurveyCTA
            assessmentKind={type}
            hasExistingScore={hasExistingScore}
            loading={type === 'mrl' ? isCreatingMrl : undefined}
            onStartClick={type === 'trl' ? () => setIsTrlModalOpen(true) : handleStartMrl}
            showCta={showStartButton || hasNoAssessments}
            survey={draftSurvey}
            technology={technology}
          />
        )}

        {type === 'trl' && (
          <TrlAssessmentHistoryList
            activeTab={activeTab}
            assessments={assessments}
            featuredAssessmentId={featuredAssessmentId}
            isLoading={isAssessmentsLoading}
            onSelectAssessment={selectAssessment}
            onSelectTrl1={() => selectAssessment(TRL1_ASSESSMENT_ID)}
            onTabChange={setActiveTab}
            showProjection={canRenderProjection}
            selectedAssessmentId={effectiveTrlAssessmentId}
            timeline={technology?.timeline}
          />
        )}

        {type === 'mrl' && (
          <MrlAssessmentHistoryList
            activeTab={activeTab === 'projection' ? 'overview' : activeTab}
            assessments={assessments}
            featuredAssessmentId={featuredAssessmentId}
            isLoading={isAssessmentsLoading}
            onSelectAssessment={selectAssessment}
            onTabChange={setActiveTab}
            selectedAssessmentId={assessmentId}
          />
        )}
      </>
    );
  };

  // Render main content
  const renderMainContent = () => {
    if (type === 'trl' && hasNoAssessments && activeTab === 'individual' && technology) {
      return (
        <AnimatePresence
          initial={false}
          mode="wait"
        >
          <motion.div
            key={`${activeTab}-${assessmentId ?? 'trl1'}`}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            initial={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <Trl1Content
              canEdit={canEdit}
              technology={technology}
            />
          </motion.div>
        </AnimatePresence>
      );
    }

    if (hasNoAssessments) {
      const emptyStateTitle = type === 'trl' ? 'No TRL Assessments' : 'No MRL Assessments';
      const emptyStateMessage =
        type === 'trl'
          ? 'Complete your first TRL assessment to unlock overview insights and history.'
          : 'Complete your first MRL assessment to unlock overview insights and history.';

      return (
        <div className="flex flex-col gap-4">
          <AccordionTile
            content={config.description}
            title={config.title}
            value={`${type}-description`}
          />
          <Tile
            className="min-h-72"
            emptyState={{ message: emptyStateMessage, title: emptyStateTitle }}
            isEmpty
          />
        </div>
      );
    }

    return (
      <AnimatePresence
        initial={false}
        mode="wait"
      >
        <motion.div
          key={`${activeTab}-${assessmentId ?? 'featured'}`}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          initial={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
        >
          {type === 'trl' ? (
            activeTab === 'overview' ? (
              <TrlOverviewContent
                onNavigateToAssessment={selectAssessment}
                technologyId={technologyId}
              />
            ) : activeTab === 'projection' ? (
              <TrlProjectionContent technologyId={technologyId} />
            ) : isTrl1Selected && technology ? (
              <Trl1Content
                canEdit={canEdit}
                technology={technology}
              />
            ) : (
              <IndividualTrlContent
                assessmentId={effectiveTrlAssessmentId}
                canEdit={canEdit}
                technologyId={technologyId}
              />
            )
          ) : activeTab === 'overview' ? (
            <MrlOverviewContent
              onNavigateToAssessment={selectAssessment}
              technologyId={technologyId}
            />
          ) : (
            <IndividualMrlContent
              assessmentId={assessmentId}
              canEdit={canEdit}
              technologyId={technologyId}
            />
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <>
      <div
        aria-hidden="true"
        className="h-0"
        ref={sidebarNavAnchorRef}
      />

      <TechnologyTabLayout
        className={TECHNOLOGY_TAB_STACK_LAYOUT_CLASS}
        mainContent={renderMainContent()}
        sidebar={renderSidebar()}
        sidebarClassName={TECHNOLOGY_TAB_SIDEBAR_WIDTH_CLASS}
        variant="sidebar-content"
      />

      {/* TRL-specific modal */}
      {type === 'trl' && (
        <StartTRLModal
          isOpen={isTrlModalOpen}
          onClose={() => setIsTrlModalOpen(false)}
          technology={technology}
        />
      )}
    </>
  );
};
